import React, { useEffect, useState } from 'react';
import { ArrowLeft, Play, Share2, Plus, Star, MapPin, Building2, Ticket, Clock, Calendar } from 'lucide-react';
import { Movie, Screening } from '../../types';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

interface ExtendedMovie extends Movie {
  rating?: number;
  year?: string | number;
  genre?: string;
  backdropUrl?: string;
  organizer?: string;
  location?: string;
}

interface MovieDetailsProps {
  movie: ExtendedMovie;
  onBack: () => void;
  onBook: (id: number) => void;
}

export const MovieDetails: React.FC<MovieDetailsProps> = ({ movie, onBack, onBook }) => {
  const [activeTab, setActiveTab] = useState<'plot' | 'cast' | 'gallery' | 'info'>('plot');
  const [screenings, setScreenings] = useState<Screening[]>([]);

  const isBookingClosed = (() => {
    const now = new Date();
    const startOk = movie.booking_start_time ? now >= new Date(movie.booking_start_time) : true;
    const stopOk = movie.booking_stop_enabled ? (!!movie.booking_stop_time && now < new Date(movie.booking_stop_time)) : true;
    return !movie.accept_booking || !startOk || !stopOk;
  })();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

    // Fetch screenings for this movie
    const fetchScreenings = async () => {
      const { data } = await supabase
        .from('screenings')
        .select('*')
        .eq('movie_id', movie.id)
        .gte('start_time', new Date().toISOString())
        .order('start_time', { ascending: true });

      if (data) {
        setScreenings(data as Screening[]);
      }
    };

    fetchScreenings();
  }, [movie.id]);

  // Group screenings by location
  const screeningsByLocation = screenings.reduce((acc, screening) => {
    const loc = screening.location || 'Unknown Location';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(screening);
    return acc;
  }, {} as Record<string, Screening[]>);

  return (
    <div className="min-h-screen w-full bg-[#1a0b0b] text-white relative overflow-x-hidden font-sans">

      {/* Dynamic Background - Fixed so it remains static while content scrolls */}
      <div className="fixed inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a0b0b] via-[#1a0b0b]/90 to-transparent z-10 w-full md:w-2/3"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1a0b0b] via-transparent to-transparent z-10"></div>
        <img
          src={movie.backdropUrl || movie.image_url}
          alt="Backdrop"
          className="w-full h-full object-cover object-center opacity-60"
        />
      </div>

      {/* Main Content Container */}
      <div className="relative z-20 w-full min-h-screen flex flex-col p-6 md:p-12 max-w-7xl mx-auto">

        {/* Header Navigation */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white uppercase text-xs font-bold tracking-widest group transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-spy-red transition-colors">
              <ArrowLeft size={14} />
            </div>
            Back
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-12 mt-4 flex-grow items-start">

          {/* Left Side: Poster - Aligned with Title */}
          <div className="hidden md:block w-1/3 relative">
            <div className="relative z-10 mt-16 transition-transform duration-500 hover:scale-[1.02]">
              <img
                src={movie.image_url}
                alt={movie.title}
                className="w-full rounded-lg shadow-2xl shadow-red-900/40 border-b-4 border-spy-red"
              />
            </div>
          </div>

          {/* Right Side: Info Content */}
          <div className="flex-1 flex flex-col pt-0">

            {/* Header Stats Row */}
            <div className="flex flex-wrap items-center gap-6 mb-4 text-xs font-bold tracking-widest text-gray-300">
              {/* Circular Rating */}
              {movie.rating !== undefined && (
                <div className="relative w-12 h-12 flex items-center justify-center">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                    <path className="text-white/10" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="2" />
                    <path className="text-spy-red" strokeDasharray={`${movie.rating * 10}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span className="absolute text-sm font-white">{movie.rating}</span>
                </div>
              )}

              <span className="bg-[#f5c518] text-black px-2 py-0.5 rounded text-[10px] font-black">IMDb</span>

              {movie.year && (
                <div className="flex items-center gap-1">
                  <MapPin size={12} className="text-spy-red" />
                  <span>{movie.year}</span>
                </div>
              )}

              {movie.genre && (
                <div className="flex items-center gap-1">
                  <span>{movie.genre}</span>
                </div>
              )}

              <div className="flex items-center gap-1">
                <Clock size={12} />
                <span>{movie.duration} min</span>
              </div>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter mb-6 leading-none">
              {movie.title}
            </h1>
            {movie.is_canceled && (
              <span className="inline-flex items-center gap-2 py-1 px-3 rounded-full bg-red-600 text-white text-xs font-bold uppercase tracking-widest mb-6">Cancelled</span>
            )}

            {/* Tabs */}
            <div className="flex gap-8 border-b border-white/10 mb-6">
              {['plot'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`pb-3 text-sm font-bold tracking-widest uppercase transition-colors relative ${activeTab === tab ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  {tab}
                  {activeTab === tab && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-spy-red shadow-[0_0_10px_rgba(211,47,47,0.8)]"></span>
                  )}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="mb-8 min-h-[100px]">
              <p className="text-gray-300 leading-relaxed text-sm md:text-base max-w-2xl">
                {movie.description}
              </p>

              <div className="flex items-center gap-1 mt-4 text-spy-red">
                {[1, 2, 3, 4].map((i) => (
                  <Star key={i} size={14} fill="currentColor" />
                ))}
                <Star size={14} className="text-gray-600" />
                <span className="text-gray-500 text-xs ml-2 font-bold tracking-wider">IMDB RATING</span>
              </div>
            </div>

            {/* Organizer Chip */}
            <div className="mb-6">
              <span className="inline-flex items-center gap-2 py-2 px-4 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white text-xs font-bold uppercase tracking-widest hover:bg-white/20 transition-colors cursor-default">
                <div className="w-5 h-5 rounded-full bg-spy-red flex items-center justify-center">
                  <Building2 size={10} />
                </div>
                Organizer: {movie.organizer || "Unknown"}
              </span>
            </div>

            {/* Locations & Showtimes */}
            {Object.keys(screeningsByLocation).length > 0 ? (
              <div className="space-y-6 mb-8 w-full max-w-3xl">
                <h3 className="text-xl font-bold uppercase tracking-widest border-l-4 border-spy-red pl-3 text-white">Available Locations</h3>
                <div className="grid grid-cols-1 gap-4">
                  {Object.entries(screeningsByLocation).map(([location, locScreenings]) => (
                    <div key={location} className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden hover:bg-white/10 transition-all duration-300">
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-spy-red/20 flex items-center justify-center text-spy-red">
                              <MapPin size={20} />
                            </div>
                            <div>
                              <h4 className="text-lg font-bold text-white">{location}</h4>
                              <p className="text-xs text-gray-400 font-medium tracking-wider">
                                {locScreenings.length} Upcoming Showings
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => { if (!movie.is_canceled && !isBookingClosed) onBook(movie.id) }}
                            disabled={!!movie.is_canceled || isBookingClosed}
                            className={`px-6 py-2 ${movie.is_canceled || isBookingClosed ? 'bg-gray-500 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500'} text-white text-xs font-bold uppercase tracking-widest rounded-lg transition-colors flex items-center gap-2`}
                          >
                            <Ticket size={14} /> {movie.is_canceled ? 'Cancelled' : isBookingClosed ? 'Closed' : 'Book Here'}
                          </button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {locScreenings.slice(0, 5).map((s) => (
                            <div key={s.id} className="px-3 py-1.5 rounded-md bg-black/40 border border-white/5 text-xs font-medium text-gray-300 flex items-center gap-2">
                              <Calendar size={10} className="text-spy-red" />
                              {format(new Date(s.start_time), 'EEE, MMM d • h:mm a')}
                            </div>
                          ))}
                          {locScreenings.length > 5 && (
                            <span className="px-3 py-1.5 text-xs text-gray-500 font-medium flex items-center">
                              +{locScreenings.length - 5} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mb-8 p-6 rounded-xl bg-white/5 border border-white/10 text-center">
                <p className="text-gray-400 text-sm tracking-wider uppercase">No upcoming screenings available</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => { if (!movie.is_canceled && !isBookingClosed) onBook(movie.id) }}
                disabled={!!movie.is_canceled || isBookingClosed}
                className={movie.is_canceled || isBookingClosed
                  ? "bg-gray-300 text-gray-700 py-4 px-8 rounded-full font-black uppercase tracking-widest cursor-not-allowed flex items-center gap-2"
                  : "bg-white text-black py-4 px-8 rounded-full font-black uppercase tracking-widest btn-spyred-hover transition-all transform hover:scale-105 shadow-xl flex items-center gap-2"}
              >
                <Ticket size={18} />
                {movie.is_canceled ? 'Cancelled' : isBookingClosed ? 'Booking Closed' : 'Book Now'}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};
